import React, { useState, useEffect } from 'react';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQuery } from 'react-query';
import { useAuth } from 'services/auth-service';
import SpaceService, { SpaceProps } from 'services/hooks/space-service';
import BuildingService, { BuildingProps } from 'services/hooks/building-service';
import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { DataGrid } from '@mui/x-data-grid';
import Checkbox from '@mui/material/Checkbox';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { toast } from 'react-toastify';
import {
  generateApiFailErrorMessage,
  generateUpdateErrorMessage,
  generateUpdateSuccessMessage
} from 'utils/helpers';
import useUpdateSpaceAlerts from './crud/useUpdateSpaceAlerts';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { colours } from 'utils/constants';
import InputField from 'components/elements/Form/Input/InputField';
import { styled } from '@mui/material/styles';

const StyledGridOverlay = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%'
}));

function CustomNoRowsOverlay() {
  return (
    <StyledGridOverlay>
      <Box sx={{ p: 1, textAlign: 'center' }}>
        <Typography>No Data</Typography>
        <Typography variant="body2">Consider selecting a different building</Typography>
      </Box>
    </StyledGridOverlay>
  );
}

type FormData = {
  isSmsAlertChecked: boolean;
  isEmailAlertChecked: boolean;
  emergencyPhoneNumbers: string;
  emergencyEmails: string;
  buildingUuid: string;
};

type UpdateSpaceAlertsProps = {
  entityName: string;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
};

const CreateSpaceAlertsSchema = yup.object().shape({
  isSmsAlertChecked: yup.boolean(),
  isEmailAlertChecked: yup.boolean(),
  emergencyPhoneNumbers: yup.string().when('isSmsAlertChecked', {
    is: true,
    then: yup
      .string()
      .required('Please enter at least one phone number')
      .test(
        'valid-phone-numbers',
        'Please enter valid phone numbers separated by commas',
        (value) => {
          if (!value) return false;
          const phoneNumbers = value.split(',').map((phone) => phone.trim());
          return phoneNumbers.every((phone) => /^\+\d{10,15}$/.test(phone));
        }
      ),
    otherwise: yup
      .string()
      .test(
        'valid-phone-numbers',
        'Please enter valid phone numbers separated by commas',
        (value) => {
          if (!value) return true;
          const phoneNumbers = value.split(',').map((phone) => phone.trim());
          return phoneNumbers.every((phone) => /^\+\d{10,15}$/.test(phone));
        }
      )
  }),
  emergencyEmails: yup.string().when('isEmailAlertChecked', {
    is: true,
    then: yup
      .string()
      .required('Please enter at least one email')
      .test('valid-emails', 'Please enter valid emails separated by commas', (value) => {
        if (!value) return false;
        const emails = value.split(',').map((email) => email.trim());
        return emails.every((email) => /^\S+@\S+\.\S+$/.test(email));
      }),
    otherwise: yup
      .string()
      .test('valid-emails', 'Please enter valid emails separated by commas', (value) => {
        if (!value) return true;
        const emails = value.split(',').map((email) => email.trim());
        return emails.every((email) => /^\S+@\S+\.\S+$/.test(email));
      })
  })
});

export default function ManageSpaceAlerts({
  entityName,
  showModal,
  setShowModal
}: UpdateSpaceAlertsProps) {
  const {
    state: { user }
  } = useAuth();
  const [filteredSpaces, setFilteredSpaces] = useState<SpaceProps[]>([]);
  const [isSmsAlertChecked, setIsSmsAlertChecked] = useState(false);
  const [isEmailAlertChecked, setIsEmailAlertChecked] = useState(false);
  const [initialSpaces, setInitialSpaces] = useState<SpaceProps[]>([]);

  const { isSuccess, data: spacesData } = useQuery<SpaceProps[], Error>(
    'spaces',
    SpaceService.findAllSpaces,
    {
      onError: () => {
        toast.error(generateApiFailErrorMessage('Spaces'));
      }
    }
  );

  const { data: buildingsData, isSuccess: isSuccessBuildings } = useQuery(
    'buildings',
    BuildingService.findAllBuildings,
    {
      enabled: user.role !== 'BUILDING',
      onError: () => {
        toast.error(generateApiFailErrorMessage('Buildings'));
      }
    }
  );

  const methods = useForm({
    resolver: yupResolver(CreateSpaceAlertsSchema),
    defaultValues: {
      isSmsAlertChecked: false,
      isEmailAlertChecked: false,
      emergencyPhoneNumbers: '',
      emergencyEmails: '',
      buildingUuid: ''
    }
  });

  useEffect(() => {
    if (spacesData) {
      const smsAlertChecked = spacesData.some((space) => space.smsAlertEnabled);
      const emailAlertChecked = spacesData.some((space) => space.emailAlertEnabled);
      setIsSmsAlertChecked(smsAlertChecked);
      setIsEmailAlertChecked(emailAlertChecked);
      methods.setValue('isSmsAlertChecked', smsAlertChecked);
      methods.setValue('isEmailAlertChecked', emailAlertChecked);
      setInitialSpaces(spacesData);
    }
  }, [spacesData, methods]);

  useEffect(() => {
    methods.setValue('isSmsAlertChecked', isSmsAlertChecked);
    methods.setValue('isEmailAlertChecked', isEmailAlertChecked);
  }, [isSmsAlertChecked, isEmailAlertChecked, methods]);

  useEffect(() => {
    if (isSuccessBuildings && buildingsData?.length > 0) {
      if (user.role === 'BUILDING') {
        methods.setValue('buildingUuid', user.uuid ?? '');
      } else {
        const firstBuilding = buildingsData[0];
        methods.setValue('buildingUuid', firstBuilding.uuid ?? '');
        methods.setValue(
          'emergencyPhoneNumbers',
          firstBuilding.emergencyPhoneNumbers?.join(', ') ?? ''
        );
        methods.setValue('emergencyEmails', firstBuilding.emergencyEmails?.join(', ') ?? '');
      }
    }
  }, [isSuccessBuildings, buildingsData, methods, user]);

  const buildingUuidWatch = methods.watch('buildingUuid');

  useEffect(() => {
    if (buildingUuidWatch && spacesData && buildingsData) {
      const filtered = spacesData.filter((space) => space.buildingUuid === buildingUuidWatch);
      setFilteredSpaces(filtered);

      const selectedBuilding = buildingsData.find(
        (building) => building.uuid === buildingUuidWatch
      );
      if (selectedBuilding) {
        methods.setValue(
          'emergencyPhoneNumbers',
          selectedBuilding.emergencyPhoneNumbers?.join(', ') ?? ''
        );
        methods.setValue('emergencyEmails', selectedBuilding.emergencyEmails?.join(', ') ?? '');
      }

      // Update SMS and Email alert checked state based on the new building's spaces
      const smsAlertChecked = filtered.some((space) => space.smsAlertEnabled);
      const emailAlertChecked = filtered.some((space) => space.emailAlertEnabled);
      setIsSmsAlertChecked(smsAlertChecked);
      setIsEmailAlertChecked(emailAlertChecked);
      methods.setValue('isSmsAlertChecked', smsAlertChecked);
      methods.setValue('isEmailAlertChecked', emailAlertChecked);
    } else {
      setFilteredSpaces([]);
      methods.setValue('emergencyPhoneNumbers', '');
      methods.setValue('emergencyEmails', '');
    }
  }, [buildingUuidWatch, spacesData, buildingsData, methods]);

  const handleCheckboxChange = (uuid: string, field: 'smsAlertEnabled' | 'emailAlertEnabled') => {
    setFilteredSpaces((prevSpaces) => {
      const updatedSpaces = prevSpaces.map((space) =>
        space.uuid === uuid ? { ...space, [field]: !space[field] } : space
      );

      setIsSmsAlertChecked(updatedSpaces.some((space) => space.smsAlertEnabled));
      setIsEmailAlertChecked(updatedSpaces.some((space) => space.emailAlertEnabled));

      return updatedSpaces;
    });
  };

  const handleSelectAllChange = (
    field: 'smsAlertEnabled' | 'emailAlertEnabled',
    isSelected: boolean
  ) => {
    setFilteredSpaces((prevSpaces) => {
      const updatedSpaces = prevSpaces.map((space) => ({
        ...space,
        [field]: isSelected
      }));

      setIsSmsAlertChecked(updatedSpaces.some((space) => space.smsAlertEnabled));
      setIsEmailAlertChecked(updatedSpaces.some((space) => space.emailAlertEnabled));

      return updatedSpaces;
    });
  };

  const columns = [
    {
      field: 'smsAlertEnabled',
      headerName: 'SMS Alert',
      width: 150,
      renderHeader: () => (
        <Box display="flex" alignItems="center">
          <Checkbox
            indeterminate={
              filteredSpaces.length > 0 &&
              filteredSpaces.some((space) => space.smsAlertEnabled) &&
              !filteredSpaces.every((space) => space.smsAlertEnabled)
            }
            checked={
              filteredSpaces.length > 0 && filteredSpaces.every((space) => space.smsAlertEnabled)
            }
            onClick={() =>
              handleSelectAllChange(
                'smsAlertEnabled',
                !filteredSpaces.every((space) => space.smsAlertEnabled)
              )
            }
          />
          <Typography
            variant="body2"
            sx={{
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              fontWeight: 'var(--unstable_DataGrid-headWeight)',
              fontSize: '0.875rem'
            }}
          >
            SMS Alerts
          </Typography>
        </Box>
      ),
      renderCell: (params: any) => (
        <Box display="flex" alignItems="center" paddingTop="4px">
          <Checkbox
            checked={params.value}
            onClick={() => handleCheckboxChange(params.row.uuid, 'smsAlertEnabled')}
          />
          <Typography
            variant="body2"
            sx={{
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              fontWeight: 'normal',
              fontSize: '0.875rem'
            }}
          >
            SMS Alert
          </Typography>
        </Box>
      ),
      sortable: false,
      filterable: false,
      disableColumnMenu: true
    },
    {
      field: 'emailAlertEnabled',
      headerName: 'Email Alert',
      width: 150,
      renderHeader: () => (
        <Box display="flex" alignItems="center">
          <Checkbox
            indeterminate={
              filteredSpaces.length > 0 &&
              filteredSpaces.some((space) => space.emailAlertEnabled) &&
              !filteredSpaces.every((space) => space.emailAlertEnabled)
            }
            checked={
              filteredSpaces.length > 0 && filteredSpaces.every((space) => space.emailAlertEnabled)
            }
            onClick={() =>
              handleSelectAllChange(
                'emailAlertEnabled',
                !filteredSpaces.every((space) => space.emailAlertEnabled)
              )
            }
          />
          <Typography
            variant="body2"
            sx={{
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              fontWeight: 'var(--unstable_DataGrid-headWeight)',
              fontSize: '0.875rem'
            }}
          >
            Email Alerts
          </Typography>
        </Box>
      ),
      renderCell: (params: any) => (
        <Box display="flex" alignItems="center" paddingTop="5px">
          <Checkbox
            checked={params.value}
            onClick={() => handleCheckboxChange(params.row.uuid, 'emailAlertEnabled')}
          />
          <Typography
            variant="body2"
            sx={{
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              fontWeight: 'normal',
              fontSize: '0.875rem'
            }}
          >
            Email Alert
          </Typography>
        </Box>
      ),
      sortable: false,
      filterable: false,
      disableColumnMenu: true
    },
    { field: 'name', headerName: 'Space', width: 150 },
    { field: 'description', headerName: 'Description', width: 200 },
    { field: 'squareFeet', headerName: 'Square Feet', width: 150 },
    { field: 'capacity', headerName: 'Capacity', width: 150 }
  ];

  const updateSpaceAlertMutation = useUpdateSpaceAlerts({
    updateQuery: SpaceService.updateSpaceAlerts,
    queryName: 'updateSpaceAlerts',
    onSuccess: () => {
      setShowModal(false);
      toast.success(generateUpdateSuccessMessage('Space Alerts'));
    },
    onError: () => {
      toast.error(generateUpdateErrorMessage('Space Alerts'));
    }
  });

  const onSubmit = async (formData: FormData) => {
    const emergencyPhoneNumbers = formData.emergencyPhoneNumbers
      .split(',')
      .map((phone: string) => phone.trim())
      .filter((phone: string) => phone.length > 0);

    const emergencyEmails = formData.emergencyEmails
      .split(',')
      .map((email: string) => email.trim())
      .filter((email: string) => email.length > 0);

    const modifiedSpaces = filteredSpaces.filter((space) => {
      const original = initialSpaces.find((s) => s.uuid === space.uuid);
      return (
        original &&
        (original.smsAlertEnabled !== space.smsAlertEnabled ||
          original.emailAlertEnabled !== space.emailAlertEnabled)
      );
    });

    const requestPayload = {
      emergencyPhoneNumbers,
      emergencyEmails,
      Spaces: modifiedSpaces.map((space) => ({
        spaceUuid: space.uuid ?? '',
        smsAlertEnabled: space.smsAlertEnabled ?? false,
        emailAlertEnabled: space.emailAlertEnabled ?? false
      }))
    };

    updateSpaceAlertMutation.mutate({
      uuid: formData.buildingUuid,
      updatedSpaceAlerts: requestPayload
    });
  };

  return isSuccess ? (
    <Dialog
      fullScreen
      open={showModal}
      onClose={() => {
        setShowModal(false);
      }}
      PaperProps={{ sx: { p: 0, bgcolor: colours.lightGrey } }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={1}
        sx={{
          bgcolor: '#FFFFFF',
          position: 'sticky',
          top: 0,
          width: '100%',
          zIndex: 1,
          px: 4,
          borderBottom: '1px solid #DFEAEA'
        }}
      >
        <Button
          onClick={() => {
            setShowModal(false);
          }}
          startIcon={<ArrowBackIcon />}
          variant="contained"
          size="small"
          sx={{
            textTransform: 'none',
            fontSize: '1rem',
            backgroundColor: colours.backButtonGrey1,
            color: '#000',
            boxShadow: 'none',
            borderRadius: '8px',
            width: '85px',
            '&:hover': { backgroundColor: colours.backButtonGrey1, boxShadow: 'none' }
          }}
        >
          Back
        </Button>
        <DialogTitle sx={{ flex: 1, textAlign: 'center', fontWeight: 600 }}>
          {entityName}
        </DialogTitle>
        <DialogActions>
          <Button
            onClick={() => {
              setShowModal(false);
            }}
            variant="contained"
            size="small"
            sx={{
              mr: 1,
              textTransform: 'none',
              fontSize: '1rem',
              backgroundColor: colours.backButtonGrey1,
              color: '#000',
              boxShadow: 'none',
              borderRadius: '8px',
              width: '90px',
              '&:hover': { backgroundColor: colours.backButtonGrey1, boxShadow: 'none' }
            }}
          >
            Discard
          </Button>
          <Button
            onClick={methods.handleSubmit(onSubmit)}
            variant="contained"
            size="small"
            sx={{
              textTransform: 'none',
              fontSize: '1rem',
              backgroundColor: colours.seafoam,
              color: '#fff',
              boxShadow: 'none',
              borderRadius: '8px',
              width: '80px',
              '&:hover': { backgroundColor: colours.seafoam, boxShadow: 'none' }
            }}
            disabled={filteredSpaces.length === 0}
          >
            Save
          </Button>
        </DialogActions>
      </Box>
      <DialogContent sx={{ bgcolor: colours.lightGrey }}>
        <Box
          p={2}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            px: { xs: 2, sm: 4, md: 20 }
          }}
        >
          {(user.role === 'SUPER' || user.role === 'ACCOUNT') &&
            isSuccessBuildings &&
            buildingsData.length > 0 && (
              <Box mb={4} sx={{ width: '100%', maxWidth: '1270px' }}>
                <FormControl fullWidth variant="outlined" sx={{ bgcolor: '#ffffff' }}>
                  <InputLabel id="building-select-label">Select a Building</InputLabel>
                  <Controller
                    name="buildingUuid"
                    control={methods.control}
                    render={({ field }) => (
                      <Select {...field} label="Select a Building" labelId="building-select-label">
                        {buildingsData.map((building: BuildingProps) => (
                          <MenuItem key={building.uuid} value={building.uuid}>
                            {building.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Box>
            )}
          <Box
            mb={4}
            sx={{
              bgcolor: '#FFFFFF',
              width: '100%',
              maxWidth: '1270px',
              p: 2,
              borderRadius: '8px',
              border: '1px solid #DFEAEA'
            }}
          >
            <Typography variant="h6">Alert Contacts</Typography>
            <Typography variant="body2" sx={{ marginBottom: '20px' }}>
              Enter comma separated values to add multiple
            </Typography>
            <InputField
              label="SMS Text Alert"
              name="emergencyPhoneNumbers"
              type="text"
              placeholder="Please enter phone numbers"
              methods={methods}
              labelClass={`text-[#26252E] font-semibold`}
              helperText="e.g. +19027891234, +19023451234, +19029871234, +19025431234"
              helperTextClass="text-[#9C9BA7]"
            />
            <InputField
              label="Email Alert"
              name="emergencyEmails"
              type="text"
              placeholder="Please enter emails"
              methods={methods}
              labelClass={`text-[#26252E] font-semibold`}
              helperText="e.g. useremail@domain.com, useremail@domain.com, useremail@domain.com, useremail@domain.com"
              helperTextClass="text-[#9C9BA7]"
            />
          </Box>
          <Box
            sx={{
              bgcolor: '#FFFFFF',
              width: '100%',
              maxWidth: '1270px',
              p: 2,
              borderRadius: '8px',
              border: '1px solid #DFEAEA'
            }}
          >
            <Typography variant="h6" sx={{ marginBottom: '20px' }}>
              Spaces to Monitor
            </Typography>
            <div style={{ height: 631, width: '100%' }}>
              <DataGrid
                rows={filteredSpaces.length > 0 ? filteredSpaces : []}
                columns={columns}
                checkboxSelection={false}
                disableColumnFilter
                disableColumnMenu
                disableColumnSelector
                getRowId={(row) => row.uuid as string}
                slots={{
                  noRowsOverlay: CustomNoRowsOverlay
                }}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                pageSizeOptions={[10, 25, 50]}
                sx={{
                  '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                    paddingTop: '15px'
                  }
                }}
              />
            </div>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  ) : (
    <LoadingSpinner />
  );
}
